var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from 'lodash';
import { TweenMax, Draggable } from 'gsap/all';
import DragAndDropHelper from 'mixins/DragAndDropHelper';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import CorrectIcon from 'components/icons/WhiteCheckmark.vue';
import DynamicContent from '../components/activity/DynamicContent.vue';
import HtmlRenderer from 'components/activity/HtmlRenderer.vue';
import NextActivityButton from 'components/activity/NextActivityButton.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import WrongIcon from 'components/icons/WhiteX.vue';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import { hasTouchedBoundings } from 'utils/activity-util';
import mixins from 'vue-typed-mixins';
const DRAG_DISTANCE_MIN = 100;
export default mixins(DragAndDropHelper()).extend({
    name: 'DragAndDropFractions',
    components: {
        BlinkingCursorText,
        CorrectIcon,
        DynamicContent,
        HtmlRenderer,
        NextActivityButton,
        TextDisplayList,
        WrongIcon,
        ActivityWrapper
    },
    computed: {
        hasGuessedAllAnswers() {
            var _a;
            return this.correctGuesses.length === ((_a = this.currentRound.dropZones) === null || _a === void 0 ? void 0 : _a.length);
        },
        arrayOfDropZoneLength() {
            var _a;
            return Array.from(Array((_a = this.currentRound.dropZones) === null || _a === void 0 ? void 0 : _a.length).keys()).reverse();
        }
    },
    methods: {
        // start activity helper methods
        getActivityState() {
            return this.getActivityStateHelper();
        },
        getKeyboardConfig() {
            return this.getKeyboardConfigHelper();
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                this.loadActivityHelper();
            });
        },
        loadAudios(activityAudios) {
            return __awaiter(this, void 0, void 0, function* () {
                return this.loadAudiosHelper(activityAudios);
            });
        },
        startActivity() {
            this.setRoundHelper();
        },
        // end activity helper methods
        // start activity specific methods
        checkIfInCorrectDropZone(guess, shouldBeCorrectGuess, guessAction) {
            const draggableElements = Array.from(document.querySelectorAll(`[id^="draggable-${guess.draggableItem.id}-"]`));
            if (!draggableElements)
                return;
            return draggableElements.map(draggableEl => {
                const dragItemCoordinates = draggableEl.getBoundingClientRect();
                const closestDropZone = this.findClosestDropArea(dragItemCoordinates);
                if (!closestDropZone)
                    return;
                const closestDropZoneId = JSON.parse(closestDropZone.id.split('-')[1]);
                const isACorrectGuess = this.correctGuesses.some(correctGuess => correctGuess.droppableItem.id === closestDropZoneId);
                if ((shouldBeCorrectGuess && isACorrectGuess) ||
                    (!shouldBeCorrectGuess && !isACorrectGuess)) {
                    return guessAction(draggableEl);
                }
            });
        },
        createDraggables() {
            let bounds = null;
            const self = this;
            const { isMobile } = this.userDevice;
            this.currentRound.draggableItems.forEach(dragContent => {
                this.arrayOfDropZoneLength.forEach(x => {
                    let hasHitBoundary = false;
                    const hasBeenGuessedCorrectly = this.isGuessedCorrectly(dragContent);
                    Draggable.create(`#draggable-${dragContent.id}-${x}`, {
                        cursor: hasBeenGuessedCorrectly ? 'auto' : 'grab',
                        bound: document.querySelector('.activity-stage'),
                        onDrag() {
                            if (hasBeenGuessedCorrectly)
                                return;
                            // work-around for iOS not firing touchend on tablet
                            if (!isMobile)
                                return;
                            const { pointerX, pointerY } = this;
                            const hasTouched = hasTouchedBoundings(pointerX, pointerY, bounds);
                            if (hasTouched && !hasHitBoundary) {
                                hasHitBoundary = true;
                                self.handleDrop(this);
                            }
                        },
                        onDragStart() {
                            if (hasBeenGuessedCorrectly)
                                return;
                            TweenMax.set(`#draggable-${dragContent.id}-${x}`, {
                                cursor: 'grabbing'
                            });
                        },
                        onDragEnd() {
                            if (hasBeenGuessedCorrectly)
                                return;
                            hasHitBoundary = false;
                            self.handleDrop(this);
                        }
                    });
                });
            });
        },
        disableElement(el) {
            Draggable.get(el).disable();
        },
        disableDraggableCorrectGuesses() {
            this.correctGuesses.forEach(correctGuess => {
                this.checkIfInCorrectDropZone(correctGuess, true, this.disableElement);
            });
        },
        findClosestDropArea(dragItemCoordinates) {
            const allDroppableAreas = this.getAllDroppableAreas();
            let closestDropZone;
            let distance;
            const { x: dragX, y: dragY } = dragItemCoordinates;
            allDroppableAreas.find((droppableArea, index) => {
                const { x: dropX, y: dropY } = droppableArea.position;
                const xDiff = Math.abs(dragX - dropX);
                const yDiff = Math.abs(dragY - dropY);
                const totalDiff = xDiff + yDiff;
                if (!index)
                    distance = totalDiff;
                if (totalDiff <= distance && totalDiff < DRAG_DISTANCE_MIN)
                    closestDropZone = droppableArea;
            });
            return closestDropZone;
        },
        getDropZonePosition(closestDropZone, draggableId) {
            const originalDraggablePos = document
                .querySelector(`.startingPoint-${draggableId}`)
                .getBoundingClientRect();
            const { position: closestDropZonePos } = closestDropZone;
            const xPos = closestDropZonePos.x - originalDraggablePos.x;
            const yPos = closestDropZonePos.y -
                originalDraggablePos.y -
                (originalDraggablePos.height - closestDropZonePos.height) / 2;
            return { xPos, yPos };
        },
        getDragContentBackground(item, index) {
            const colors = this.checkIfInCorrectDropZone({ draggableItem: item }, true, draggableEl => {
                const isCorrect = this.correctGuesses.some(guess => guess.draggableItem.id === item.id);
                if (draggableEl && isCorrect)
                    return 'white';
                return this.defaultColor;
            });
            return colors === null || colors === void 0 ? void 0 : colors[index];
        },
        getIsCorrect(guessedAnswer) {
            return guessedAnswer.droppableItem.correctItemIds.includes(guessedAnswer.draggableItem.id);
        },
        handleDrop(context) {
            const { target } = context;
            const draggableId = target.id.split('-')[1];
            const draggableIndex = target.id.split('-')[2];
            const draggableItem = this.getAssociatedDraggableItem(draggableId);
            if (!draggableItem)
                return;
            const dragItemCoordinates = target.getBoundingClientRect();
            const closestDropZone = this.findClosestDropArea(dragItemCoordinates);
            if (closestDropZone) {
                const closestDropZoneId = JSON.parse(closestDropZone.id.split('-')[1]);
                this.removeFromGuessed(draggableItem, draggableIndex);
                const alreadyGuessed = [...this.guessed, ...this.correctGuesses].some(item => item.droppableItem.id === closestDropZoneId);
                if (alreadyGuessed) {
                    this.resetDraggable(target);
                    return;
                }
                const draggableItemWithIndex = Object.assign(Object.assign({}, draggableItem), { draggableIndex });
                this.handleMoveToDropZone(target, closestDropZone, draggableItemWithIndex, draggableId);
                return;
            }
            this.resetDraggable(target);
            this.removeFromGuessed(draggableItem, draggableIndex);
        },
        handleMoveToDropZone(target, closestDropZone, draggableItem, draggableId) {
            const { id: dropZoneElId } = closestDropZone;
            const dropAreaId = dropZoneElId.split('-')[1];
            const droppableItem = this.getAssociatedDroppableItem(dropAreaId);
            const guessedArr = [...this.guessed].filter(guessed => guessed.droppableItem !== droppableItem);
            this.guessed = _.uniq([...guessedArr, { draggableItem, droppableItem }]);
            const { xPos, yPos } = this.getDropZonePosition(closestDropZone, draggableId);
            this.moveDraggableToDropZone(target, xPos, yPos);
            return;
        },
        isGuessedCorrectly(droppableItem) {
            if (!droppableItem)
                return false;
            return this.correctGuesses.some(item => item.droppableItem.id === droppableItem.id);
        },
        isGuessedIncorrectly(droppableItem) {
            if (!droppableItem)
                return false;
            const isInWrongGuess = this.wrongGuesses.some(item => item.droppableItem.id === droppableItem.id);
            const isGuessed = this.guessed.some(item => item.droppableItem.id === droppableItem.id);
            const isEmpty = !isGuessed && !this.isGuessedCorrectly(droppableItem) && this.isSubmitted;
            return isInWrongGuess || isEmpty;
        },
        removeFromGuessed(draggableItem, draggableIndex) {
            this.guessed = [...this.guessed]
                .map(guess => {
                if (guess.draggableItem.id === draggableItem.id &&
                    guess.draggableItem.draggableIndex === draggableIndex)
                    return;
                return guess;
            })
                .filter((x) => x !== undefined);
        },
        resetWrongPieces() {
            this.wrongGuesses.forEach(wrongGuess => {
                this.checkIfInCorrectDropZone(wrongGuess, false, this.resetDraggable);
            });
        }
    }
});
